/**
 * Notifications éphémères.
 *
 * `toast.erreur(e)` affiche le **message métier** renvoyé par l’API
 * (ARCHITECTURE §9.4) : sur un 409, l’utilisateur lit « Le siège F12 est déjà
 * occupé pour cette représentation. », jamais « une erreur est survenue ».
 * Les erreurs restent affichées deux fois plus longtemps que les succès, et
 * peuvent être épinglées (`duree: 0`) quand l’action a échoué au guichet.
 */

import type { ReactNode } from 'react'
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react'

import { cn } from '../lib/cn'
import { messageErreur } from '../lib/api'

export type TypeToast = 'succes' | 'erreur' | 'avertissement' | 'info'

export interface OptionsToast {
  readonly titre?: ReactNode | undefined
  /** Millisecondes avant disparition. `0` = reste jusqu’au clic. */
  readonly duree?: number | undefined
}

interface ToastAffiche extends OptionsToast {
  readonly id: number
  readonly type: TypeToast
  readonly message: ReactNode
}

const DUREES: Readonly<Record<TypeToast, number>> = {
  succes: 4_000,
  info: 5_000,
  avertissement: 7_000,
  erreur: 9_000,
}

export interface ApiToast {
  readonly succes: (message: ReactNode, options?: OptionsToast) => void
  readonly info: (message: ReactNode, options?: OptionsToast) => void
  readonly avertissement: (message: ReactNode, options?: OptionsToast) => void
  /** Accepte un texte ou n’importe quelle erreur attrapée (`ApiError`…). */
  readonly erreur: (erreur: unknown, options?: OptionsToast) => void
  readonly fermer: (id: number) => void
}

const ContexteToast = createContext<ApiToast | null>(null)

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastAffiche[]>([])
  const compteur = useRef(0)
  const minuteries = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const fermer = useCallback((id: number) => {
    const minuterie = minuteries.current.get(id)
    if (minuterie !== undefined) {
      clearTimeout(minuterie)
      minuteries.current.delete(id)
    }
    setToasts((precedents) => precedents.filter((toast) => toast.id !== id))
  }, [])

  const afficher = useCallback(
    (type: TypeToast, message: ReactNode, options?: OptionsToast) => {
      compteur.current += 1
      const id = compteur.current
      const duree = options?.duree ?? DUREES[type]

      setToasts((precedents) => [
        // Au-delà de quatre, le plus ancien s’efface : on ne noie pas l’écran.
        ...precedents.slice(-3),
        { id, type, message, ...options },
      ])

      if (duree > 0) {
        minuteries.current.set(
          id,
          setTimeout(() => {
            fermer(id)
          }, duree),
        )
      }
    },
    [fermer],
  )

  const api = useMemo<ApiToast>(
    () => ({
      succes: (message, options) => { afficher('succes', message, options) },
      info: (message, options) => { afficher('info', message, options) },
      avertissement: (message, options) => { afficher('avertissement', message, options) },
      erreur: (erreur, options) => {
        const message = typeof erreur === 'string' ? erreur : messageErreur(erreur)
        afficher('erreur', message, options)
      },
      fermer,
    }),
    [afficher, fermer],
  )

  return (
    <ContexteToast value={api}>
      {children}
      <ZoneToasts toasts={toasts} onFermer={fermer} />
    </ContexteToast>
  )
}

export function useToast(): ApiToast {
  const contexte = use(ContexteToast)
  if (contexte === null) {
    throw new Error('useToast doit être utilisé à l’intérieur de <ToastProvider>.')
  }
  return contexte
}

/* -------------------------------------------------------------------------- */
/* Rendu                                                                      */
/* -------------------------------------------------------------------------- */

const STYLES: Readonly<Record<TypeToast, string>> = {
  succes: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  erreur: 'border-red-200 bg-red-50 text-red-900',
  avertissement: 'border-amber-200 bg-amber-50 text-amber-900',
  info: 'border-slate-200 bg-white text-slate-900',
}

const ICONES: Readonly<Record<TypeToast, string>> = {
  succes: 'm4 10.5 4 4 8-8',
  erreur: 'm6 6 8 8M14 6l-8 8',
  avertissement: 'M10 6v5m0 3h.01',
  info: 'M10 9v5m0-8h.01',
}

function ZoneToasts({
  toasts,
  onFermer,
}: {
  readonly toasts: readonly ToastAffiche[]
  readonly onFermer: (id: number) => void
}) {
  return (
    <div
      // `polite` : une notification n’interrompt pas la saisie en cours.
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 p-3 sm:inset-x-auto sm:top-auto sm:right-0 sm:bottom-0 sm:items-end sm:p-4"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.type === 'erreur' ? 'alert' : 'status'}
          className={cn(
            'animate-glissement pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-md border px-4 py-3 shadow-lg',
            STYLES[toast.type],
          )}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            className="mt-0.5 size-5 shrink-0"
            aria-hidden="true"
          >
            <circle cx="10" cy="10" r="8.5" className="opacity-30" />
            <path d={ICONES[toast.type]} strokeLinecap="round" strokeLinejoin="round" />
          </svg>

          <div className="min-w-0 flex-1 text-sm">
            {toast.titre !== undefined && <p className="font-semibold">{toast.titre}</p>}
            <p className={cn(toast.titre !== undefined && 'mt-0.5')}>{toast.message}</p>
          </div>

          <button
            type="button"
            onClick={() => { onFermer(toast.id) }}
            aria-label="Fermer la notification"
            className="-m-1 rounded p-1 opacity-60 transition-opacity hover:opacity-100"
          >
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" className="size-4">
              <path d="m5 5 10 10M15 5 5 15" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}
